import {
  Row, Col, Card, Progress, List, Tag, Typography, Space,
  Avatar, Divider, Button, Dropdown, Menu, Select, Popover
} from "antd";
import {
  UserOutlined, ProjectOutlined, CheckSquareOutlined, TeamOutlined,
  FileTextOutlined, DollarOutlined, ClockCircleOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined, CalendarOutlined,
  FileAddOutlined, UserAddOutlined, ThunderboltOutlined,
  WalletOutlined, ArrowUpOutlined, ArrowDownOutlined, FireOutlined,
  CrownOutlined, ApartmentOutlined, HomeOutlined,
  LoginOutlined, LogoutOutlined, FieldTimeOutlined, CodeOutlined,
  BranchesOutlined, BugOutlined, DeploymentUnitOutlined, 
  UnorderedListOutlined, RocketOutlined,
  ScheduleOutlined, SendOutlined, DownOutlined,
  SettingOutlined, LockOutlined, FileSearchOutlined, FundOutlined, ShopOutlined,
  DashboardOutlined,
  BarChartOutlined,
  FilterOutlined
} from "@ant-design/icons";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import { AuthContext } from "../../Auth/AuthContext";
import { useContext, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import AddCustomerModal from "../Customers/Add";
import AddProjectModal from "../projects/Add";
import TaskFormModal from "../Task/Add";
import AddExpenseModel from "../Expense/AddExpenseModel";
import AddDeveloperModal from "../Developers/Add";
import ApplyLeave from "../Leave/applyleave";
import AddCampassoffModal from "../Campassoff/Add";
import Addovertimemodal from "../Overtime/Add";
import Chatbot from "../Chatbot/Chatbot";
import { getDashboardData } from "../../utils/API"; // New API function

const { Title, Text } = Typography;

dayjs.extend(customParseFormat);

// ==================== TYPE DEFINITIONS ====================

interface StatCardType {
  label: string;
  value: number | string;
  trend: string;
  up: boolean;
  icon: React.ReactNode;
  iconBg?: string;
  isText?: boolean;
  totalAmount?: number | null;
}

interface QuickActionType {
  label: string;
  icon: React.ReactNode;
  color?: string;
  grad?: string;
  path?: string;
}

interface EventType {
  type: string;
  label: string;
  time: string;
  color: string;
  tag: string;
}

interface AdminTaskType {
  label: string;
  value: number;
  total: number;
  color: string;
  percent: number;
}

interface AdminHRMSType {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}

interface CRMType {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  total?: string;
}

interface AccountingType {
  label: string;
  value: string;
  sub: string;
  color: string;
  icon: React.ReactNode;
}

interface TaskSummaryType {
  label: string;
  val: string;
  color: string;
}

interface DevTaskType {
  id: number;
  taskId?: string;
  task?: any;
  title: string;
  project: string;
  status: "Completed" | "In Progress" | "Pending" | "Overdue";
  deadline: string;
  icon: React.ReactNode;
}

interface DevTaskStatType {
  label: string;
  val: number;
  total: number;
  color: string;
}

interface StatusConfigType {
  color: string;
  bg: string;
  border: string;
}

interface AttendanceType {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
}

interface DevHRMSType {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
}

interface SalesChartPoint {
  label: string;
  value: number;
}

interface SalesChartData {
  defaultFilter: "yearly" | "monthly" | "quarterly";
  filters: Array<{ label: string; value: "yearly" | "monthly" | "quarterly" }>;
  series: {
    yearly: SalesChartPoint[];
    monthly: SalesChartPoint[];
    quarterly: SalesChartPoint[];
  };
  totalConvertedSales: number;
}

// ==================== STYLES & CONSTANTS ====================

const P = {
  violet: "#7c3aed",
  indigo: "#4f46e5",
  sky: "#0ea5e9",
  teal: "#0d9488",
  emerald: "#059669",
  amber: "#d97706",
  rose: "#e11d48",
  slate: "#475569",
} as const;

const gradients = {
  adminPurple: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  adminPink: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  adminBlue: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  adminGreen: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  adminOrange: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
  adminCyan: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
  violet: "linear-gradient(135deg,#7c3aed 0%,#4f46e5 100%)",
  sky: "linear-gradient(135deg,#0ea5e9 0%,#0d9488 100%)",
  emerald: "linear-gradient(135deg,#059669 0%,#0ea5e9 100%)",
  amber: "linear-gradient(135deg,#f59e0b 0%,#ef4444 100%)",
  rose: "linear-gradient(135deg,#e11d48 0%,#f59e0b 100%)",
  indigo: "linear-gradient(135deg,#4f46e5 0%,#0ea5e9 100%)",
} as const;
// dashboard feachure

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
const monthNamesFull = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;
const getMonthKeyForBackend = (selectedMonth: string, dataObj: Record<string, any>) => {
  if (selectedMonth in dataObj) return selectedMonth;
  const monthIndex = monthNames.indexOf(selectedMonth as any);
  const fullMonth = monthIndex >= 0 ? monthNamesFull[monthIndex] : monthNamesFull.find((m) => m.toLowerCase() === selectedMonth.toLowerCase());
  if (fullMonth && fullMonth in dataObj) return fullMonth;
  const numericMonth = monthIndex >= 0 ? String(monthIndex + 1) : String(monthNamesFull.findIndex((m) => m.toLowerCase() === selectedMonth.toLowerCase()) + 1);
  if (numericMonth && numericMonth in dataObj) return numericMonth;
  const lowerKey = selectedMonth.toLowerCase();
  const matchingKey = Object.keys(dataObj).find(
    (key) => key.toLowerCase() === lowerKey || key.toLowerCase() === fullMonth?.toLowerCase() || key === numericMonth
  );
  return matchingKey || selectedMonth;
};

const getMonthQueryParam = (selectedMonth: string) => {
  const monthIndex = monthNames.indexOf(selectedMonth as any);
  if (monthIndex >= 0) return String(monthIndex + 1);
  const fullIndex = monthNamesFull.findIndex((m) => m.toLowerCase() === selectedMonth.toLowerCase());
  if (fullIndex >= 0) return String(fullIndex + 1);
  return selectedMonth;
};

const getQuarterQueryParam = (selectedQuarter: string) => {
  const quarterMap: Record<string, string> = {
    "Jan-Mar": "Q1",
    "Apr-Jun": "Q2",
    "Jul-Sep": "Q3",
    "Oct-Dec": "Q4",
  };
  return quarterMap[selectedQuarter] || selectedQuarter;
};

const normalizeSalesChartData = (data: any, fallbackLabel?: string) => {
  // Handle null/undefined data
  if (!data) return [];
  
  if (Array.isArray(data)) {
    // Filter out invalid items in the array
    return data.filter((item: any) => item && (item.label !== undefined || item.value !== undefined));
  }
  
  if (typeof data === "number" || typeof data === "string") {
    return [{ label: fallbackLabel || "Value", value: Number(data) }];
  }
  
  if (data && typeof data === "object") {
    return Object.entries(data).map(([label, value]) => {
      const rawValue = value as any;
      const numValue = typeof value === "object" ? Number(rawValue?.value ?? rawValue?.total ?? 0) : Number(value);
      return {
        label,
        value: isNaN(numValue) ? 0 : numValue,
      };
    }).filter((item: any) => item.label !== undefined && item.value !== undefined);
  }
  
  return [];
};

const getQuarterlyTotalsFromYearlyData = (quarterData: Record<string, any>) => {
  if (!quarterData || typeof quarterData !== "object") return [];
  
  return Object.entries(quarterData).map(([label, data]) => {
    if (!data) return { label, value: 0 };
    
    if (Array.isArray(data)) {
      const total = data.reduce((sum: number, item: any) => {
        return sum + (Number(item?.value ?? item?.total ?? 0) || 0);
      }, 0);
      return { label, value: Math.round(total) };
    }
    if (data && typeof data === "object") {
      const nestedValues = Object.values(data);
      const hasNestedCollections = nestedValues.some((item) => Array.isArray(item) || (item && typeof item === "object"));
      if (hasNestedCollections) {
        const total = nestedValues.reduce((sum: number, item: any) => {
          if (Array.isArray(item)) {
            return sum + item.reduce((nestedSum: number, nestedItem: any) => nestedSum + (Number(nestedItem?.value ?? nestedItem?.total ?? 0) || 0), 0);
          }
          if (item && typeof item === "object") {
            return sum + (Number(item?.value ?? item?.total ?? 0) || 0);
          }
          return sum + (Number(item) || 0);
        }, 0);
        return { label, value: Math.round(total) };
      }
      return { label, value: Number(data?.value ?? data?.total ?? 0) || 0 };
    }
    return { label, value: Number(data) || 0 };
  });
};

const styles = {
  dashboardWrapper: {
    padding: "clamp(16px, 2vw, 24px)",
    background: "#f8fafc",
    minHeight: "100vh",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase" as const,
    color: "#64748b",
    marginBottom: 16,
    marginTop: 8,
  } as React.CSSProperties,
  card: {
    borderRadius: 20,
    border: "none",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    overflow: "hidden",
    background: "#ffffff",
    height: "100%",
  } as React.CSSProperties,
  eventItem: {
    padding: "12px 0",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
  } as React.CSSProperties,
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  } as React.CSSProperties,
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#1e293b",
  } as React.CSSProperties,
  viewAllLink: {
    color: "#64748b",
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
  } as React.CSSProperties,
};

const devCard: React.CSSProperties = {
  borderRadius: 20,
  border: "none",
  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  background: "#ffffff",
  height: "100%",
};

const iconBox = (bg: string, size: number = 44): React.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: 12,
  background: bg,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: size * 0.45,
  flexShrink: 0,
  boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
});

// ==================== DATA ====================

const menuItems = [
  { icon: <DashboardOutlined />, label: "Dashboard" },
  { icon: <UserOutlined />, label: "Customers" },
  { icon: <ProjectOutlined />, label: "Projects" },
  { icon: <CheckSquareOutlined />, label: "Tasks" },
  { icon: <ApartmentOutlined />, label: "Master" },
  { icon: <TeamOutlined />, label: "HRMS" },
  { icon: <FundOutlined />, label: "CRM" },
  { icon: <BarChartOutlined />, label: "Analytics" },
  { icon: <WalletOutlined />, label: "Accounting" },
  { icon: <ShopOutlined />, label: "AMC Maintenance" },
  { icon: <CalendarOutlined />, label: "Calendar" },
  { icon: <FileSearchOutlined />, label: "Report" },
  { icon: <LockOutlined />, label: "Roles & Permission" },
  { icon: <SettingOutlined />, label: "Settings" },
  { icon: <Chatbot/>, label: "Chatbot"}
];

const adminQuickActions: QuickActionType[] = [
  { label: "Create Invoice", icon: <FileAddOutlined style={{ fontSize: 20 }} />, color: "linear-gradient(135deg,#667eea,#764ba2)", path: "/invoice/add" },
  { label: "Add Customer", icon: <UserAddOutlined style={{ fontSize: 20 }} />, color: "linear-gradient(135deg,#43e97b,#38f9d7)", path: "/customers" },
  { label: "Add Project", icon: <ApartmentOutlined style={{ fontSize: 20 }} />, color: "linear-gradient(135deg,#4facfe,#00f2fe)", path: "/projects" },
  { label: "Create Task", icon: <CheckSquareOutlined style={{ fontSize: 20 }} />, color: "linear-gradient(135deg,#f093fb,#f5576c)", path: "/taskS" },
  { label: "Add Expense", icon: <WalletOutlined style={{ fontSize: 20 }} />, color: "linear-gradient(135deg,#fa709a,#fee140)", path: "/expense" },
  { label: "Add Employee", icon: <TeamOutlined style={{ fontSize: 20 }} />, color: "linear-gradient(135deg,#30cfd0,#330867)", path: "/developers" },
];

const upcomingEvents: EventType[] = [
  { type: "meeting", label: "Q2 Strategy Sync", time: "Today, 3:00 PM", color: "#667eea", tag: "Meeting" },
  { type: "deadline", label: "Nexus Platform Delivery", time: "Tomorrow, EOD", color: "#f5576c", tag: "Deadline" },
  { type: "holiday", label: "Company Foundation Day", time: "Mar 15", color: "#43e97b", tag: "Holiday" },
  { type: "meeting", label: "Client Onboarding — Acme", time: "Mar 16, 11:00 AM", color: "#4facfe", tag: "Meeting" },
  { type: "deadline", label: "Tax Filing Submission", time: "Mar 20", color: "#fa709a", tag: "Deadline" },
];

const statusConfig: Record<string, StatusConfigType> = {
  "Completed": { color: "#059669", bg: "#f0fdf4", border: "#bbf7d0" },
  "In Progress": { color: "#0ea5e9", bg: "#f0f9ff", border: "#bae6fd" },
  "Pending": { color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  "Overdue": { color: "#e11d48", bg: "#fff1f2", border: "#fecdd3" },
};

const devQuickActions: QuickActionType[] = [
  { label: "Submit Leave", icon: <CalendarOutlined />, grad: gradients.violet, path: "/leave" },
  { label: "Campus Off", icon: <HomeOutlined />, grad: gradients.sky, path: "/campus-off" },
  { label: "Request Overtime", icon: <FieldTimeOutlined />, grad: gradients.emerald, path: "/overtime" },
  { label: "View My Tasks", icon: <UnorderedListOutlined />, grad: gradients.indigo, path: "/tasks" },
];

// ==================== HELPER COMPONENTS ====================

interface SectionLabelProps {
  children: React.ReactNode;
}

const SectionLabel: React.FC<SectionLabelProps> = ({ children }) => {
  return (
    <div style={styles.sectionTitle}>
      {children}
    </div>
  );
};

interface CardTitleProps {
  icon: React.ReactNode;
  color: string;
  children: React.ReactNode;
}

const CardTitle: React.FC<CardTitleProps> = ({ icon, color, children }) => {
  return (
    <span style={{ fontWeight: 600, fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ color }}>{icon}</span>
      {children}
    </span>
  );
};

const pill = (color: string, filled: boolean = false): React.CSSProperties => ({
  borderRadius: 30,
  padding: "4px 12px",
  fontSize: 12,
  fontWeight: 500,
  background: filled ? color : color + "10",
  color: filled ? "#fff" : color,
  border: filled ? "none" : `1px solid ${color}20`,
});

// Helper function to format amount
const formatAmount = (amount: number): string => {
  return new Intl.NumberFormat('en-IN').format(amount);
};
const formatCompact = (amount: number): string => {
  const n = Number(amount) || 0;
  const abs = Math.abs(n);
  if (abs >= 10000000) return (n / 10000000).toFixed(abs >= 100000000 ? 0 : 1) + 'Cr';
  if (abs >= 100000) return (n / 100000).toFixed(abs >= 1000000 ? 0 : 1) + 'L';
  if (abs >= 1000) return (n / 1000).toFixed(0) + 'K';
  return String(Math.round(n));
};

// ==================== MAIN COMPONENT ====================

const DashboardContent: React.FC = () => {
  const navigate = useNavigate();
  const [hoveredAction, setHoveredAction] = useState<number | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [isAddExpenseModalOpen, setIsAddExpenseModalOpen] = useState(false);
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [isCampassOffModalOpen, setIsCampassOffModalOpen] = useState(false);
  const [isOvertimeModalOpen, setIsOvertimeModalOpen] = useState(false);
  const { authData } = useContext(AuthContext);
  
  // Dynamic state for dashboard data
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [salesChartFilter, setSalesChartFilter] = useState<"yearly" | "monthly" | "quarterly">("yearly");
  const [salesChartYear, setSalesChartYear] = useState<number>(dayjs().year());
  const [salesChartMonth, setSalesChartMonth] = useState<string>(monthNames[0]);
  const [expensesChartFilter, setExpensesChartFilter] = useState<"yearly" | "monthly" | "quarterly">("yearly");
  const [expensesChartYear, setExpensesChartYear] = useState<number>(dayjs().year());
  const [expensesChartMonth, setExpensesChartMonth] = useState<string>(monthNames[0]);
  const [receiptsChartFilter, setReceiptsChartFilter] = useState<"yearly" | "monthly" | "quarterly">("yearly");
  const [receiptsChartYear, setReceiptsChartYear] = useState<number>(dayjs().year());
  const [receiptsChartMonth, setReceiptsChartMonth] = useState<string>(monthNames[0]);
  const [leadsChartFilter, setLeadsChartFilter] = useState<"yearly" | "monthly" | "quarterly">("yearly");
  const [leadsChartYear, setLeadsChartYear] = useState<number>(dayjs().year());
  const [leadsChartMonth, setLeadsChartMonth] = useState<string>(monthNames[0]);
  const [taskDateFilterType, setTaskDateFilterType] = useState<"yearly" | "monthly" | "daily">("yearly");
  const [taskDateYear, setTaskDateYear] = useState<number>(dayjs().year());
  const [taskDateMonth, setTaskDateMonth] = useState<typeof monthNames[number]>(monthNames[0]);
  const [taskDateDay, setTaskDateDay] = useState<number>(dayjs().date());
  const [filterPopoverVisible, setFilterPopoverVisible] = useState<boolean>(false);
  const [taskFilterApplied, setTaskFilterApplied] = useState<boolean>(false);
  const [appliedFilterLabel, setAppliedFilterLabel] = useState<string>("");
  const hasFetchedDashboard = useRef(false);
  
  const isAdmin = authData?.roleId?.roleId === 1;
  const isDeveloper = authData?.roleId?.roleId === 2;

  const currentDashboardUser = () => {
    const data = localStorage.getItem("authData");
    if (!data) return null;

    const parsed = JSON.parse(data);
    return {
      ...parsed.user,
      role: parsed.roleId?.name?.toLowerCase() || "",
    };
  };

  const getRawTaskId = (task: any) => {
    return [
      task?.taskId,
      task?.details?.taskId,
      task?.task?.taskId,
      task?.details?.task?.taskId,
    ]
      .map((value: any) => String(value ?? "").trim())
      .find(Boolean) || "";
  };

  const parseYearFromLabel = (label: string): number | null => {
    const match = label.match(/(20\d{2})$/);
    return match ? Number(match[1]) : null;
  };

  const getDaysInMonth = (year: number, month: typeof monthNames[number]): number => {
    const monthIndex = monthNames.indexOf(month);
    if (monthIndex === -1) return 31;
    return dayjs(new Date(year, monthIndex + 1, 0)).date();
  };

  const getSalesChartYearOptions = useCallback((): number[] => {
    const currentYear = dayjs().year();
    const yearRange: number[] = [];
    for (let year = currentYear; year >= currentYear - 9; year -= 1) {
      yearRange.push(year);
    }
    return yearRange;
  }, []);

  const getSalesChartMonthOptions = useCallback((): string[] => {
    return [...monthNames];
  }, []);

  const getSalesChartSeries = () => {
    const salesChart = dashboardData?.salesChart;
    if (!salesChart) return [];

    if (salesChartFilter === "yearly") {
      const yearlyByYear = salesChart.series?.yearlyByYear?.[salesChartYear] ?? salesChart.yearlyByYear?.[salesChartYear];
      if (yearlyByYear) {
        const normalized = normalizeSalesChartData(yearlyByYear, String(salesChartYear));
        if (normalized.length) return normalized;
      }
      const fallbackYearly = salesChart.series?.yearly || salesChart.yearly;
      if (fallbackYearly) return normalizeSalesChartData(fallbackYearly, String(salesChartYear));
    }

    if (salesChartFilter === "monthly") {
      const monthlyByYear = salesChart.series?.monthlyByYear?.[salesChartYear] ?? salesChart.monthlyByYear?.[salesChartYear];
      if (monthlyByYear && typeof monthlyByYear === "object") {
        const monthKey = getMonthKeyForBackend(salesChartMonth, monthlyByYear);
        const data = monthlyByYear[monthKey] || monthlyByYear;
        const normalized = normalizeSalesChartData(data, salesChartMonth);
        if (normalized.length) return normalized;
      }
      const fallbackMonthly = salesChart.series?.monthly || salesChart.monthly;
      if (fallbackMonthly) return normalizeSalesChartData(fallbackMonthly, salesChartMonth);
    }

    if (salesChartFilter === "quarterly") {
      const quarterlyByYear = salesChart.series?.quarterlyByYear?.[salesChartYear] ?? salesChart.quarterlyByYear?.[salesChartYear];
      if (quarterlyByYear) {
        const totals = getQuarterlyTotalsFromYearlyData(quarterlyByYear);
        if (totals.length) return totals;
        const normalized = normalizeSalesChartData(quarterlyByYear, String(salesChartYear));
        if (normalized.length) return normalized;
      }
      const fallbackQuarterly = salesChart.series?.quarterly || salesChart.quarterly;
      if (fallbackQuarterly) return normalizeSalesChartData(fallbackQuarterly, String(salesChartYear));
    }

    return salesChart.series?.[salesChartFilter] || salesChart[salesChartFilter] || [];
  };

  const getExpensesChartSeries = () => {
    const expensesChart = dashboardData?.expensesChart;
    if (!expensesChart) return [];

    if (expensesChartFilter === "yearly") {
      const yearlyByYear = expensesChart.series?.yearlyByYear?.[expensesChartYear] ?? expensesChart.yearlyByYear?.[expensesChartYear];
      if (yearlyByYear) {
        const normalized = normalizeSalesChartData(yearlyByYear, String(expensesChartYear));
        if (normalized.length) return normalized;
      }
      const fallbackYearly = expensesChart.series?.yearly || expensesChart.yearly;
      if (fallbackYearly) return normalizeSalesChartData(fallbackYearly, String(expensesChartYear));
    }

    if (expensesChartFilter === "monthly") {
      const monthlyByYear = expensesChart.series?.monthlyByYear?.[expensesChartYear] ?? expensesChart.monthlyByYear?.[expensesChartYear];
      if (monthlyByYear && typeof monthlyByYear === "object") {
        const monthKey = getMonthKeyForBackend(expensesChartMonth, monthlyByYear);
        const data = monthlyByYear[monthKey] || monthlyByYear;
        const normalized = normalizeSalesChartData(data, expensesChartMonth);
        if (normalized.length) return normalized;
      }
      const fallbackMonthly = expensesChart.series?.monthly || expensesChart.monthly;
      if (fallbackMonthly) return normalizeSalesChartData(fallbackMonthly, expensesChartMonth);
    }

    if (expensesChartFilter === "quarterly") {
      const quarterlyByYear = expensesChart.series?.quarterlyByYear?.[expensesChartYear] ?? expensesChart.quarterlyByYear?.[expensesChartYear];
      if (quarterlyByYear) {
        const totals = getQuarterlyTotalsFromYearlyData(quarterlyByYear);
        if (totals.length) return totals;
        const normalized = normalizeSalesChartData(quarterlyByYear, String(expensesChartYear));
        if (normalized.length) return normalized;
      }
      const fallbackQuarterly = expensesChart.series?.quarterly || expensesChart.quarterly;
      if (fallbackQuarterly) return normalizeSalesChartData(fallbackQuarterly, String(expensesChartYear));
    }

    return expensesChart.series?.[expensesChartFilter] || expensesChart[expensesChartFilter] || [];
  };

  const getReceiptsChartSeries = () => {
    const receiptsChart = dashboardData?.receiptsChart;
    if (!receiptsChart) return [];

    if (Array.isArray(receiptsChart)) {
      return receiptsChart;
    }

    if (receiptsChartFilter === "yearly") {
      const yearlyByYear = receiptsChart.series?.yearlyByYear?.[receiptsChartYear] ?? receiptsChart.yearlyByYear?.[receiptsChartYear];
      if (yearlyByYear) {
        const normalized = normalizeSalesChartData(yearlyByYear, String(receiptsChartYear));
        if (normalized.length) return normalized;
      }
      const fallbackYearly = receiptsChart.series?.yearly || receiptsChart.yearly;
      if (fallbackYearly) return normalizeSalesChartData(fallbackYearly, String(receiptsChartYear));
    }

    if (receiptsChartFilter === "monthly") {
      const monthlyByYear = receiptsChart.series?.monthlyByYear?.[receiptsChartYear] ?? receiptsChart.monthlyByYear?.[receiptsChartYear];
      if (monthlyByYear && typeof monthlyByYear === "object") {
        const monthKey = getMonthKeyForBackend(receiptsChartMonth, monthlyByYear);
        const data = monthlyByYear[monthKey] || monthlyByYear;
        const normalized = normalizeSalesChartData(data, receiptsChartMonth);
        if (normalized.length) return normalized;
      }
      const fallbackMonthly = receiptsChart.series?.monthly || receiptsChart.monthly;
      if (fallbackMonthly) return normalizeSalesChartData(fallbackMonthly, receiptsChartMonth);
    }

    if (receiptsChartFilter === "quarterly") {
      const quarterlyByYear = receiptsChart.series?.quarterlyByYear?.[receiptsChartYear] ?? receiptsChart.quarterlyByYear?.[receiptsChartYear];
      if (quarterlyByYear) {
        const totals = getQuarterlyTotalsFromYearlyData(quarterlyByYear);
        if (totals.length) return totals;
        const normalized = normalizeSalesChartData(quarterlyByYear, String(receiptsChartYear));
        if (normalized.length) return normalized;
      }
      const fallbackQuarterly = receiptsChart.series?.quarterly || receiptsChart.quarterly;
      if (fallbackQuarterly) return normalizeSalesChartData(fallbackQuarterly, String(receiptsChartYear));
    }

    return receiptsChart.series?.[receiptsChartFilter] || receiptsChart[receiptsChartFilter] || [];
  };

  const getLeadsChartSeries = () => {
    const leadsChart = dashboardData?.leadsChart;
    if (!leadsChart) return [];

    const resolve = (bucket: any) => {
      if (!bucket) return [];
      if (Array.isArray(bucket)) return bucket.filter((item: any) => item && item.label !== undefined);
      return normalizeSalesChartData(bucket, String(leadsChartYear));
    };

    if (leadsChartFilter === "yearly") {
      const yearlyByYear = leadsChart.series?.yearlyByYear?.[leadsChartYear] ?? leadsChart.yearlyByYear?.[leadsChartYear];
      if (yearlyByYear) {
        const arr = resolve(yearlyByYear);
        if (arr.length) return arr;
      }
      const fallbackYearly = leadsChart.series?.yearly || leadsChart.yearly;
      if (fallbackYearly) return resolve(fallbackYearly);
    }

    if (leadsChartFilter === "monthly") {
      const monthlyByYear = leadsChart.series?.monthlyByYear?.[leadsChartYear] ?? leadsChart.monthlyByYear?.[leadsChartYear];
      if (monthlyByYear && typeof monthlyByYear === "object") {
        const monthKey = getMonthKeyForBackend(leadsChartMonth, monthlyByYear);
        const data = monthlyByYear[monthKey] || monthlyByYear;
        const arr = resolve(data);
        if (arr.length) return arr;
      }
      const fallbackMonthly = leadsChart.series?.monthly || leadsChart.monthly;
      if (fallbackMonthly) return resolve(fallbackMonthly);
    }

    if (leadsChartFilter === "quarterly") {
      const quarterlyByYear = leadsChart.series?.quarterlyByYear?.[leadsChartYear] ?? leadsChart.quarterlyByYear?.[leadsChartYear];
      if (quarterlyByYear) {
        const arr = resolve(quarterlyByYear);
        if (arr.length) return arr;
      }
      const fallbackQuarterly = leadsChart.series?.quarterly || leadsChart.quarterly;
      if (fallbackQuarterly) return resolve(fallbackQuarterly);
    }

    return leadsChart.series?.[leadsChartFilter] || leadsChart[leadsChartFilter] || [];
  };

  const buildSalesChartParams = useCallback((options?: {
    filterType?: "yearly" | "monthly" | "quarterly";
    year?: number;
    month?: string;
  }) => {
    const params: Record<string, any> = {};
    if (options?.filterType) params.salesFilterType = options.filterType;
    if (options?.year !== undefined) params.salesYear = options.year;
    if (options?.month && options.filterType === "monthly") params.salesMonth = getMonthQueryParam(options.month);
    return params;
  }, []);

  const buildExpensesChartParams = useCallback((options?: {
    filterType?: "yearly" | "monthly" | "quarterly";
    year?: number;
    month?: string;
  }) => {
    const params: Record<string, any> = {};
    if (options?.filterType) params.expensesFilterType = options.filterType;
    if (options?.year !== undefined) params.expensesYear = options.year;
    if (options?.month && options.filterType === "monthly") {
      params.expensesMonth = getMonthQueryParam(options.month);
    }
    return params;
  }, []);

  const buildReceiptsChartParams = useCallback((options?: {
    filterType?: "yearly" | "monthly" | "quarterly";
    year?: number;
    month?: string;
  }) => {
    const params: Record<string, any> = {};
    if (options?.filterType) params.receiptsFilterType = options.filterType;
    if (options?.year !== undefined) params.receiptsYear = options.year;
    if (options?.month && options.filterType === "monthly") {
      params.receiptsMonth = getMonthQueryParam(options.month);
    }
    return params;
  }, []);

  const buildLeadsChartParams = useCallback((options?: {
    filterType?: "yearly" | "monthly" | "quarterly";
    year?: number;
    month?: string;
  }) => {
    const params: Record<string, any> = {};
    if (options?.filterType) params.leadsFilterType = options.filterType;
    if (options?.year !== undefined) params.leadsYear = options.year;
    if (options?.month && options.filterType === "monthly") {
      params.leadsMonth = getMonthQueryParam(options.month);
    }
    return params;
  }, []);

  useEffect(() => {
    const years = getSalesChartYearOptions();
    setSalesChartYear((prevYear) => (years.includes(prevYear) ? prevYear : years[0] || dayjs().year()));
    setExpensesChartYear((prevYear) => (years.includes(prevYear) ? prevYear : years[0] || dayjs().year()));
    setReceiptsChartYear((prevYear) => (years.includes(prevYear) ? prevYear : years[0] || dayjs().year()));
    setLeadsChartYear((prevYear) => (years.includes(prevYear) ? prevYear : years[0] || dayjs().year()));

    const monthOptions = getSalesChartMonthOptions();
    setSalesChartMonth((prevMonth) => (monthOptions.includes(prevMonth) ? prevMonth : monthOptions[0] || monthNames[0]));
    setExpensesChartMonth((prevMonth) => (monthOptions.includes(prevMonth) ? prevMonth : monthOptions[0] || monthNames[0]));
    setReceiptsChartMonth((prevMonth) => (monthOptions.includes(prevMonth) ? prevMonth : monthOptions[0] || monthNames[0]));
    setLeadsChartMonth((prevMonth) => (monthOptions.includes(prevMonth) ? prevMonth : monthOptions[0] || monthNames[0]));
  }, [dashboardData, getSalesChartYearOptions, getSalesChartMonthOptions]);

  useEffect(() => {
    if (salesChartFilter === "monthly") {
      const monthOptions = getSalesChartMonthOptions();
      if (!monthOptions.includes(salesChartMonth)) {
        setSalesChartMonth(monthOptions[0] || monthNames[0]);
      }
    }
  }, [salesChartFilter, salesChartMonth]);

  useEffect(() => {
    const years = getSalesChartYearOptions();
    if (!years.includes(expensesChartYear)) {
      setExpensesChartYear(years[0] || dayjs().year());
    }
    const monthOptions = getSalesChartMonthOptions();
    if (!monthOptions.includes(expensesChartMonth)) {
      setExpensesChartMonth(monthOptions[0] || monthNames[0]);
    }
  }, [expensesChartFilter, expensesChartYear, expensesChartMonth]);

  useEffect(() => {
    const years = getSalesChartYearOptions();
    if (!years.includes(receiptsChartYear)) {
      setReceiptsChartYear(years[0] || dayjs().year());
    }
    const monthOptions = getSalesChartMonthOptions();
    if (!monthOptions.includes(receiptsChartMonth)) {
      setReceiptsChartMonth(monthOptions[0] || monthNames[0]);
    }
  }, [receiptsChartFilter, receiptsChartYear, receiptsChartMonth]);

  useEffect(() => {
    const years = getSalesChartYearOptions();
    if (!years.includes(leadsChartYear)) {
      setLeadsChartYear(years[0] || dayjs().year());
    }
    const monthOptions = getSalesChartMonthOptions();
    if (!monthOptions.includes(leadsChartMonth)) {
      setLeadsChartMonth(monthOptions[0] || monthNames[0]);
    }
  }, [leadsChartFilter, leadsChartYear, leadsChartMonth]);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch all dashboard data in a single API call
  const fetchDashboardData = async (
    showLoader: boolean = true,
    params?: {
      salesFilterType?: "yearly" | "monthly" | "quarterly";
      salesYear?: number;
      salesMonth?: string;
      expensesFilterType?: "yearly" | "monthly" | "quarterly";
      expensesYear?: number;
      expensesMonth?: string;
      receiptsFilterType?: "yearly" | "monthly" | "quarterly";
      receiptsYear?: number;
      receiptsMonth?: string;
      leadsFilterType?: "yearly" | "monthly" | "quarterly";
      leadsYear?: number;
      leadsMonth?: string;
      taskDateFilterType?: "yearly" | "monthly" | "daily";
      taskDateYear?: number;
      taskDateMonth?: string;
      taskDateDay?: number;
    }
  ) => {
    try {
      if (showLoader) {
        setLoading(true);
      }
      const response = await getDashboardData(params);
      if (response && response.success) {
        const isInitialDashboardLoad = !dashboardData;
        setDashboardData(response.data);
        if (isInitialDashboardLoad && response.data?.salesChart?.defaultFilter) {
          setSalesChartFilter(response.data.salesChart.defaultFilter);
        }
        if (isInitialDashboardLoad && response.data?.expensesChart?.defaultFilter) {
          setExpensesChartFilter(response.data.expensesChart.defaultFilter);
        }
        if (isInitialDashboardLoad && response.data?.receiptsChart?.defaultFilter) {
          setReceiptsChartFilter(response.data.receiptsChart.defaultFilter);
        }
        if (isInitialDashboardLoad && response.data?.leadsChart?.defaultFilter) {
          setLeadsChartFilter(response.data.leadsChart.defaultFilter);
        }
        if (!hasFetchedDashboard.current) {
          hasFetchedDashboard.current = true;
        }
      } else {
        console.error("Failed to fetch dashboard data:", response);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (authData) {
      fetchDashboardData();
    }
  }, [authData]);

  useEffect(() => {
    if (!authData || !hasFetchedDashboard.current) return;

    const salesParams = buildSalesChartParams({
      filterType: salesChartFilter,
      year: salesChartYear,
      month: salesChartFilter === "monthly" ? salesChartMonth : undefined,
    });

    const expensesParams = buildExpensesChartParams({
      filterType: expensesChartFilter,
      year: expensesChartYear,
      month: expensesChartFilter === "monthly" ? expensesChartMonth : undefined,
    });

    const receiptsParams = buildReceiptsChartParams({
      filterType: receiptsChartFilter,
      year: receiptsChartYear,
      month: receiptsChartFilter === "monthly" ? receiptsChartMonth : undefined,
    });

    const leadsParams = buildLeadsChartParams({
      filterType: leadsChartFilter,
      year: leadsChartYear,
      month: leadsChartFilter === "monthly" ? leadsChartMonth : undefined,
    });

    const taskParams = taskFilterApplied ? {
      taskDateFilterType,
      taskDateYear,
      taskDateMonth: taskDateFilterType === "monthly" || taskDateFilterType === "daily" ? taskDateMonth : undefined,
      taskDateDay: taskDateFilterType === "daily" ? taskDateDay : undefined,
    } : {};

    fetchDashboardData(false, { ...salesParams, ...expensesParams, ...receiptsParams, ...leadsParams, ...taskParams });
  }, [salesChartFilter, salesChartYear, salesChartMonth, expensesChartFilter, expensesChartYear, expensesChartMonth, receiptsChartFilter, receiptsChartYear, receiptsChartMonth, leadsChartFilter, leadsChartYear, leadsChartMonth, taskFilterApplied, taskDateFilterType, taskDateYear, taskDateMonth, taskDateDay, buildSalesChartParams, buildExpensesChartParams, buildReceiptsChartParams, buildLeadsChartParams]);

  useEffect(() => {
    const refreshDashboard = () => {
      if (document.visibilityState === "visible" && authData && hasFetchedDashboard.current) {
        const salesParams = buildSalesChartParams({
          filterType: salesChartFilter,
          year: salesChartYear,
          month: salesChartFilter === "monthly" ? salesChartMonth : undefined,
        });

        const expensesParams = buildExpensesChartParams({
          filterType: expensesChartFilter,
          year: expensesChartYear,
          month: expensesChartFilter === "monthly" ? expensesChartMonth : undefined,
        });

        const receiptsParams = buildReceiptsChartParams({
          filterType: receiptsChartFilter,
          year: receiptsChartYear,
          month: receiptsChartFilter === "monthly" ? receiptsChartMonth : undefined,
        });

        const leadsParams = buildLeadsChartParams({
          filterType: leadsChartFilter,
          year: leadsChartYear,
          month: leadsChartFilter === "monthly" ? leadsChartMonth : undefined,
        });

        const taskParams = taskFilterApplied ? {
          taskDateFilterType,
          taskDateYear,
          taskDateMonth: taskDateFilterType === "monthly" || taskDateFilterType === "daily" ? taskDateMonth : undefined,
          taskDateDay: taskDateFilterType === "daily" ? taskDateDay : undefined,
        } : {};

        fetchDashboardData(false, { ...salesParams, ...expensesParams, ...receiptsParams, ...leadsParams, ...taskParams });
      }
    };

    const handleWindowFocus = () => {
      if (authData && hasFetchedDashboard.current) {
        const salesParams = buildSalesChartParams({
          filterType: salesChartFilter,
          year: salesChartYear,
          month: salesChartFilter === "monthly" ? salesChartMonth : undefined,
        });

        const expensesParams = buildExpensesChartParams({
          filterType: expensesChartFilter,
          year: expensesChartYear,
          month: expensesChartFilter === "monthly" ? expensesChartMonth : undefined,
        });

        const receiptsParams = buildReceiptsChartParams({
          filterType: receiptsChartFilter,
          year: receiptsChartYear,
          month: receiptsChartFilter === "monthly" ? receiptsChartMonth : undefined,
        });

        const leadsParams = buildLeadsChartParams({
          filterType: leadsChartFilter,
          year: leadsChartYear,
          month: leadsChartFilter === "monthly" ? leadsChartMonth : undefined,
        });

        const taskParams = taskFilterApplied ? {
          taskDateFilterType,
          taskDateYear,
          taskDateMonth: taskDateFilterType === "monthly" || taskDateFilterType === "daily" ? taskDateMonth : undefined,
          taskDateDay: taskDateFilterType === "daily" ? taskDateDay : undefined,
        } : {};

        fetchDashboardData(false, { ...salesParams, ...expensesParams, ...receiptsParams, ...leadsParams, ...taskParams });
      }
    };

    document.addEventListener("visibilitychange", refreshDashboard);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      document.removeEventListener("visibilitychange", refreshDashboard);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [authData, salesChartFilter, salesChartYear, salesChartMonth, expensesChartFilter, expensesChartYear, expensesChartMonth, receiptsChartFilter, receiptsChartYear, receiptsChartMonth, taskFilterApplied, taskDateFilterType, taskDateYear, taskDateMonth, taskDateDay, buildSalesChartParams, buildExpensesChartParams, buildReceiptsChartParams]);

  // Create dynamic admin stat cards from dashboard data
  const getAdminStatCards = (): StatCardType[] => {
    if (!dashboardData) return [];

    if (Array.isArray(dashboardData.overviewCards)) {
      const iconMap: Record<string, { icon: React.ReactNode; iconBg: string; isText?: boolean }> = {
        customers: { icon: <UserOutlined />, iconBg: gradients.adminPurple },
        projects: { icon: <ProjectOutlined />, iconBg: gradients.adminPink },
        tasks: { icon: <CheckSquareOutlined />, iconBg: gradients.adminBlue },
        employees: { icon: <TeamOutlined />, iconBg: gradients.adminGreen },
        invoices: { icon: <FileTextOutlined />, iconBg: gradients.adminOrange },
        revenue: { icon: <DollarOutlined />, iconBg: gradients.adminCyan, isText: true },
      };

      return dashboardData.overviewCards.map((card: any) => {
        const iconConfig = iconMap[card.key] || { icon: <DashboardOutlined />, iconBg: gradients.adminPurple };
        const displayValue = card.key === 'revenue'
          ? '₹' + new Intl.NumberFormat('en-IN').format(card.value || 0)
          : card.isCurrencyMillion
          ? '$' + (Number(card.value || 0) / 1000000).toFixed(1) + 'M'
          : card.value;

        return {
          label: card.label,
          value: displayValue,
          totalAmount: card.totalAmount || null,
          trend: card.trend,
          up: card.up,
          icon: iconConfig.icon,
          iconBg: iconConfig.iconBg,
          isText: iconConfig.isText,
        };
      });
    }
    
    const { stats, trends } = dashboardData;
    
    return [
      {
        label: "Total Customers",
        value: stats.totalCustomers,
        trend: trends.customers.trend,
        up: trends.customers.up,
        icon: <UserOutlined />,
        iconBg: gradients.adminPurple,
      },
      {
        label: "Active Projects",
        value: stats.activeProjects,
        trend: trends.projects.trend,
        up: trends.projects.up,
        icon: <ProjectOutlined />,
        iconBg: gradients.adminPink,
      },
      {
        label: "Total Tasks",
        value: stats.totalTasks,
        trend: trends.tasks.trend,
        up: trends.tasks.up,
        icon: <CheckSquareOutlined />,
        iconBg: gradients.adminBlue,
      },
      {
        label: "Total Employees",
        value: stats.totalEmployees,
        trend: trends.employees.trend,
        up: trends.employees.up,
        icon: <TeamOutlined />,
        iconBg: gradients.adminGreen,
      },
      {
        label: "Total Invoices",
        value: stats.totalInvoices,
        trend: trends.invoices.trend,
        up: trends.invoices.up,
        icon: <FileTextOutlined />,
        iconBg: gradients.adminOrange,
      },
      {
        label: "Total Revenue",
        value: '₹' + formatAmount(stats.totalRevenue),
        trend: trends.revenue.trend,
        up: trends.revenue.up,
        icon: <DollarOutlined />,
        iconBg: gradients.adminCyan,
      },
    ];
  };

  // Create admin HRMS data from dashboard data
  const getAdminHrmsData = (): AdminHRMSType[] => {
    if (!dashboardData) return [];
    
    const { stats, hrms } = dashboardData;
    
    return [
      { label: "Total Employees", value: stats.totalEmployees, icon: <TeamOutlined />, color: "#667eea", bgColor: "#ede9fe" },
      { label: "Present Today", value: hrms.presentToday, icon: <CheckCircleOutlined />, color: "#52c41a", bgColor: "#f0fdf4" },
      { label: "On Leave", value: hrms.onLeave, icon: <ClockCircleOutlined />, color: "#faad14", bgColor: "#fffbeb" },
      // { label: "Late Punch-In", value: hrms.latePunchIn, icon: <ExclamationCircleOutlined />, color: "#ff4d4f", bgColor: "#fff1f0" },
    ];
  };

  // Create admin task data from dashboard data
  const getAdminTaskData = (): AdminTaskType[] => {
    if (!dashboardData) return [];
    
    const { tasksOverview } = dashboardData;
    const total = tasksOverview.total || 1;
    
    return [
      { label: "Completed", value: tasksOverview.completed, total, color: "#52c41a", percent: Math.round((tasksOverview.completed / total) * 100) },
      { label: "Pending", value: tasksOverview.pending, total, color: "#faad14", percent: Math.round((tasksOverview.pending / total) * 100) },
      { label: "Overdue", value: tasksOverview.overdue, total, color: "#ff4d4f", percent: Math.round((tasksOverview.overdue / total) * 100) },
    ];
  };

  // const getTaskSummary = (): TaskSummaryType[] => {
  //   if (!dashboardData) return [];
    
  //   const { tasksOverview } = dashboardData;
  //   const total = tasksOverview.total || 1;
    
  //   return [
  //     { label: "Completed", val: `${Math.round((tasksOverview.completed / total) * 100)}%`, color: "#52c41a" },
  //     { label: "Pending", val: `${Math.round((tasksOverview.pending / total) * 100)}%`, color: "#faad14" },
  //     { label: "Overdue", val: `${Math.round((tasksOverview.overdue / total) * 100)}%`, color: "#ff4d4f" },
  //   ];
  // };

  const getExpensesChartConfig = () => {
    const expensesChart: any = dashboardData?.expensesChart;
    const selectedSeries = getExpensesChartSeries();

    // Validate data before creating chart config
    if (!selectedSeries || !Array.isArray(selectedSeries) || selectedSeries.length === 0) {
      return {
        totalExpensesAmount: 0,
        options: {
          tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
          grid: { left: 20, right: 20, top: 24, bottom: 20, containLabel: true },
          xAxis: { type: "category", data: [] },
          yAxis: { type: "value" },
          series: [{ type: "bar", data: [] }],
        },
      };
    }

    // Filter out invalid data points
    const validSeries = selectedSeries.filter((item: any) => item && item.label !== undefined && item.value !== undefined);

    return {
      totalExpensesAmount: validSeries.reduce((sum: number, item: any) => sum + (Number(item.value) || 0), 0),
      options: {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { left: 20, right: 20, top: 24, bottom: 20, containLabel: true },
        xAxis: {
          type: "category",
          data: validSeries.map((item: any) => item.label),
          axisLine: { lineStyle: { color: "#cbd5e1" } },
          axisTick: { show: false },
          axisLabel: { color: "#64748b", fontSize: 11, interval: expensesChartFilter === "monthly" ? "auto" : 0 },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          axisLine: { show: false },
          splitLine: { lineStyle: { color: "#e2e8f0" } },
          axisLabel: {
            color: "#94a3b8",
            fontSize: 11,
            formatter: (value: number) => "Rs " + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value),
          },
        },
        series: [
          {
            type: "bar",
            barWidth: expensesChartFilter === "monthly" ? 14 : 28,
            data: validSeries.map((item: any) => ({
              value: item.value,
              itemStyle: {
                borderRadius: [10, 10, 0, 0],
                color: expensesChartFilter === "quarterly" ? "#dc2626" : new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: "#ef4444" },
                  { offset: 1, color: "#f59e0b" },
                ]),
              },
            })),
            label: { show: true, position: "top", color: "#334155", fontSize: 11, formatter: '{c}' },
          },
        ],
      },
    };
  };

  const getReceiptsChartConfig = () => {
    const selectedSeries = getReceiptsChartSeries();

    // Validate data before creating chart config
    if (!selectedSeries || !Array.isArray(selectedSeries) || selectedSeries.length === 0) {
      return {
        totalReceiptsAmount: 0,
        options: {
          tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
          grid: { left: 20, right: 20, top: 24, bottom: 20, containLabel: true },
          xAxis: { type: "category", data: [] },
          yAxis: { type: "value" },
          series: [{ type: "bar", data: [] }],
        },
      };
    }

    // Filter out invalid data points
    const validSeries = selectedSeries.filter((item: any) => item && item.label !== undefined && item.value !== undefined);

    return {
      totalReceiptsAmount: validSeries.reduce((sum: number, item: any) => sum + (Number(item.value) || 0), 0),
      options: {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { left: 20, right: 20, top: 24, bottom: 20, containLabel: true },
        xAxis: {
          type: "category",
          data: validSeries.map((item: any) => item.label),
          axisLine: { lineStyle: { color: "#cbd5e1" } },
          axisTick: { show: false },
          axisLabel: { color: "#64748b", fontSize: 11, interval: receiptsChartFilter === "monthly" ? "auto" : 0 },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          axisLine: { show: false },
          splitLine: { lineStyle: { color: "#e2e8f0" } },
          axisLabel: {
            color: "#94a3b8",
            fontSize: 11,
            formatter: (value: number) => "Rs " + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value),
          },
        },
        series: [
          {
            type: "bar",
            barWidth: receiptsChartFilter === "monthly" ? 14 : 28,
            data: validSeries.map((item: any) => ({
              value: item.value,
              itemStyle: {
                borderRadius: [10, 10, 0, 0],
                color: receiptsChartFilter === "quarterly" ? "#047857" : new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: "#4f46e5" },
                  { offset: 1, color: "#0ea5e9" },
                ]),
              },
            })),
            label: { show: true, position: "top", color: "#334155", fontSize: 11 },
          },
        ],
      },
    };
  };

  const getSalesChartConfig = () => {
    const salesChart: SalesChartData | undefined = dashboardData?.salesChart;
    const selectedSeries = getSalesChartSeries();

    // Validate data before creating chart config
    if (!selectedSeries || !Array.isArray(selectedSeries) || selectedSeries.length === 0) {
      return {
        totalConvertedSales: salesChart?.totalConvertedSales || 0,
        options: {
          tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
          grid: { left: 20, right: 20, top: 24, bottom: 20, containLabel: true },
          xAxis: { type: "category", data: [] },
          yAxis: { type: "value" },
          series: [{ type: "bar", data: [] }],
        },
      };
    }

    // Filter out invalid data points
    const validSeries = selectedSeries.filter((item: any) => item && item.label !== undefined && item.value !== undefined);

    return {
      totalConvertedSales: salesChart?.totalConvertedSales || 0,
      options: {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { left: 20, right: 20, top: 24, bottom: 20, containLabel: true },
        xAxis: {
          type: "category",
          data: validSeries.map((item: any) => item.label),
          axisLine: { lineStyle: { color: "#cbd5e1" } },
          axisTick: { show: false },
          axisLabel: { color: "#64748b", fontSize: 11, interval: salesChartFilter === "monthly" ? "auto" : 0 },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          axisLine: { show: false },
          splitLine: { lineStyle: { color: "#e2e8f0" } },
          axisLabel: { color: "#94a3b8", fontSize: 11 },
        },
        series: [
          {
            type: "bar",
            barWidth: salesChartFilter === "monthly" ? 14 : 28,
            data: validSeries.map((item: any) => ({
              value: item.value,
              itemStyle: {
                borderRadius: [10, 10, 0, 0],
                color: salesChartFilter === "quarterly" ? "#0f766e" : new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: "#4f46e5" },
                  { offset: 1, color: "#0ea5e9" },
                ]),
              },
            })),
            label: { show: true, position: "top", color: "#334155", fontSize: 11 },
          },
        ],
      },
    };
  };

  const getLeadsChartConfig = () => {
    const leadsChart = dashboardData?.leadsChart;
    const selectedSeries = getLeadsChartSeries();

    const validSeries = Array.isArray(selectedSeries)
      ? selectedSeries.filter((item: any) => item && item.label !== undefined)
      : [];
    const labels = validSeries.map((item: any) => item.label);
    const totals = validSeries.map((item: any) => Number(item?.total ?? item?.value ?? 0) || 0);
    const converted = validSeries.map((item: any) => Number(item?.converted ?? 0) || 0);
    const barWidth = leadsChartFilter === "monthly" ? 10 : 18;

    const totalSeries = {
      name: "Total Lead Amount",
      type: "bar",
      barWidth,
      label: { show: true, position: "top", color: "#4338ca", fontSize: 10, formatter: (p: any) => formatCompact(Number(p.value) || 0) },
      data: totals.map((value) => ({
        value,
        itemStyle: {
          borderRadius: [6, 6, 0, 0],
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "#6366f1" },
            { offset: 1, color: "#818cf8" },
          ]),
        },
      })),
    };

    const convertedSeries = {
      name: "Converted to Sales",
      type: "bar",
      barWidth,
      label: { show: true, position: "top", color: "#047857", fontSize: 10, formatter: (p: any) => formatCompact(Number(p.value) || 0) },
      data: converted.map((value) => ({
        value,
        itemStyle: {
          borderRadius: [6, 6, 0, 0],
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "#10b981" },
            { offset: 1, color: "#34d399" },
          ]),
        },
      })),
    };

    return {
      totalLeadsAmount: leadsChart?.totalLeadsAmount || 0,
      totalConvertedAmount: leadsChart?.totalConvertedAmount || 0,
      options: {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          formatter: (params: any) => {
            if (!Array.isArray(params) || params.length === 0) return "";
            const lines = params
              .filter((p: any) => p && p.seriesName)
              .map((p: any) => `${p.marker} ${p.seriesName}: Rs ${formatAmount(Number(p.value) || 0)}`);
            return [params[0]?.axisValue, ...lines].filter(Boolean).join("<br/>");
          },
        },
        legend: { data: ["Total Lead Amount", "Converted to Sales"], top: 0, textStyle: { color: "#64748b" } },
        grid: { left: 20, right: 20, top: 36, bottom: 20, containLabel: true },
        xAxis: {
          type: "category",
          data: labels,
          axisLine: { lineStyle: { color: "#cbd5e1" } },
          axisTick: { show: false },
          axisLabel: { color: "#64748b", fontSize: 11, interval: leadsChartFilter === "monthly" ? "auto" : 0 },
        },
        yAxis: {
          type: "value",
          axisLine: { show: false },
          splitLine: { lineStyle: { color: "#e2e8f0" } },
          axisLabel: { color: "#94a3b8", fontSize: 11 },
        },
        series: [totalSeries, convertedSeries],
      },
    };
  };

  // Get HRMS stats for developer
  const getDeveloperHrmsStats = () => {
    if (!dashboardData || dashboardData.role !== 'developer') return { leaveBalance: 0, leaveRequests: 0, overtimeHours: 0, campassOffApproved: 0 };
    return dashboardData.hrms;
  };

  // Get attendance stats for developer
  const getDeveloperAttendanceStats = () => {
    if (!dashboardData || dashboardData.role !== 'developer') {
      return {
        punchIn: "-",
        punchOut: "-",
        hoursToday: "0h 00m",
        status: "Absent",
        expectedHours: "8h",
        remainingHours: "~8h 00m",
        progressPercent: 0,
      };
    }
    return dashboardData.attendance;
  };

  // Get developer task stats
  const getDeveloperTaskStats = (): DevTaskStatType[] => {
    if (!dashboardData || dashboardData.role !== 'developer') {
      return [
        { label: "Completed", val: 0, total: 0, color: P.emerald },
        { label: "In Progress", val: 0, total: 0, color: P.sky },
        { label: "To Do", val: 0, total: 0, color: P.amber },
        { label: "Overdue", val: 0, total: 0, color: P.rose },
      ];
    }
    return dashboardData.taskStats;
  };

  // Get developer today tasks
  const getDeveloperTodayTasks = (): DevTaskType[] => {
    if (!dashboardData || dashboardData.role !== 'developer') return [];
    return dashboardData.todayTasks.map((task: any, index: number) => ({
      ...task,
      id: index + 1
    }));
  };

  // Get developer stats for stat cards
  const getDevStatCards = (): StatCardType[] => {
    if (!dashboardData || dashboardData.role !== 'developer') {
      return [
        { label: "Tasks Assigned", value: 0, trend: "+0", up: true, iconBg: gradients.violet, icon: <UnorderedListOutlined />, isText: false },
        { label: "Tasks Completed", value: 0, trend: "+0", up: true, iconBg: gradients.emerald, icon: <CheckCircleOutlined />, isText: false },
        { label: "Pending Tasks", value: 0, trend: "0 overdue", up: false, iconBg: gradients.amber, icon: <ClockCircleOutlined />, isText: false },
        { label: "Attendance", value: "Absent", trend: "Not punched in", up: false, iconBg: gradients.sky, icon: <UserOutlined />, isText: true },
      ];
    }

    if (Array.isArray(dashboardData.overviewCards)) {
      const iconMap: Record<string, { icon: React.ReactNode; iconBg: string; isText?: boolean }> = {
        tasksAssigned: { icon: <UnorderedListOutlined />, iconBg: gradients.violet },
        tasksCompleted: { icon: <CheckCircleOutlined />, iconBg: gradients.emerald },
        pendingTasks: { icon: <ClockCircleOutlined />, iconBg: gradients.amber },
        attendance: { icon: <UserOutlined />, iconBg: gradients.sky, isText: true },
      };

      return dashboardData.overviewCards.map((card: any) => {
        const iconConfig = iconMap[card.key] || { icon: <DashboardOutlined />, iconBg: gradients.violet };
        return {
          label: card.label,
          value: card.value,
          trend: card.trend,
          up: card.up,
          icon: iconConfig.icon,
          iconBg: iconConfig.iconBg,
          isText: iconConfig.isText,
        };
      });
    }
    
    const { stats, taskStats } = dashboardData;
    const pendingTasks = (taskStats.find((t: any) => t.label === "In Progress")?.val || 0) + 
                         (taskStats.find((t: any) => t.label === "To Do")?.val || 0);
    
    return [
      { label: "Tasks Assigned", value: stats.tasksAssigned, trend: "+0", up: true, iconBg: gradients.violet, icon: <UnorderedListOutlined />, isText: false },
      { label: "Tasks Completed", value: stats.tasksCompleted, trend: "+0", up: true, iconBg: gradients.emerald, icon: <CheckCircleOutlined />, isText: false },
      { label: "Pending Tasks", value: pendingTasks, trend: "0 overdue", up: false, iconBg: gradients.amber, icon: <ClockCircleOutlined />, isText: false },
      { label: "Attendance", value: stats.attendance, trend: "Today's status", up: stats.attendance === "Present", iconBg: gradients.sky, icon: <UserOutlined />, isText: true },
    ];
  };

  // Create developer attendance data array
  const getDeveloperAttendanceData = (): AttendanceType[] => {
    const attendanceStats = getDeveloperAttendanceStats();
    return [
      { label: "Punch In", value: attendanceStats.punchIn, icon: <LoginOutlined />, color: P.emerald },
      { label: "Punch Out", value: attendanceStats.punchOut, icon: <LogoutOutlined />, color: P.slate },
      { label: "Hours Today", value: attendanceStats.hoursToday, icon: <FieldTimeOutlined />, color: P.sky },
      { label: "Status", value: attendanceStats.status, icon: <CheckCircleOutlined />, color: P.violet },
    ];
  };

  // Create developer HRMS data array
  const getDeveloperHrmsData = (): DevHRMSType[] => {
    const hrmsStats = getDeveloperHrmsStats();
    return [
      { label: "Leave Balance", value: `${hrmsStats.leaveBalance} days`, icon: <CalendarOutlined />, color: P.violet },
      { label: "Leave Requests", value: `${hrmsStats.leaveRequests} pending`, icon: <FileTextOutlined />, color: P.amber },
      { label: "Overtime Hours", value: `${hrmsStats.overtimeHours} hrs`, icon: <FieldTimeOutlined />, color: P.emerald },
      { label: "Campus Off Req.", value: `${hrmsStats.campassOffApproved} approved`, icon: <HomeOutlined />, color: P.teal },
    ];
  };

  const formatValue = (value: number | string): string => {
   if (typeof value === 'number') {
     return value.toLocaleString();
   }
   return value;

  };

  const crmStats = dashboardData?.crm || {
    totalLeads: 0,
    totalLeadsAmount: 0,
    newToday: 0,
    converted: 0,
    followUpsToday: 0,
    newTodayTotalAmount: 0,
    convertedTotalAmount: 0,
    followUpTotalAmount: 0
  };

  //const leadAmount = dashboardData?.leadAmount || 0;

  const crmData: CRMType[] = [
    { label: "Total Leads", value: formatValue(crmStats.totalLeads), icon: <UserOutlined />, color: "#f5576c", total:  "₹" + new Intl.NumberFormat('en-IN').format(crmStats.totalLeadsAmount)},
    { label: "New Today", value: formatValue(crmStats.newToday), icon: <FireOutlined />, color: "#fa8c16", total:  "₹" + new Intl.NumberFormat('en-IN').format(crmStats.newTodayTotalAmount) },
    { label: "Converted", value: formatValue(crmStats.converted), icon: <CrownOutlined />, color: "#722ed1", total:  "₹" + new Intl.NumberFormat('en-IN').format(crmStats.convertedTotalAmount) },
    { label: "Follow-Ups", value: formatValue(crmStats.followUpsToday), icon: <ScheduleOutlined />, color: "#1677ff", total:  "₹" + new Intl.NumberFormat('en-IN').format(crmStats.followUpTotalAmount) },
  ];

  // Responsive breakpoints
  const isMobile = windowWidth < 576;
  const isTablet = windowWidth >= 576 && windowWidth < 768;
  const isSmallLaptop = windowWidth >= 768 && windowWidth < 992;
  const isMediumLaptop = windowWidth >= 992 && windowWidth < 1200;
  const isLargeLaptop = windowWidth >= 1200 && windowWidth < 1600;

  const handleDeveloperQuickAction = (action: QuickActionType) => {
    switch (action.label) {
      case "Submit Leave":
        setIsLeaveModalOpen(true);
        return;
      case "Campus Off":
        setIsCampassOffModalOpen(true);
        return;
      case "Request Overtime":
        setIsOvertimeModalOpen(true);
        return;
      default:
        if (action.path) {
          navigate(action.path);
        }
    }
  };

  const handleAdminQuickAction = (action: QuickActionType) => {
    switch (action.label) {
      case 'Add Customer':
        setIsAddCustomerModalOpen(true);
        return;
      case 'Add Project':
        setIsAddProjectModalOpen(true);
        return;
      case 'Create Task':
        setIsAddTaskModalOpen(true);
        return;
      case 'Add Expense':
        setIsAddExpenseModalOpen(true);
        return;
      case 'Add Employee':
        setIsAddEmployeeModalOpen(true);
        return;
      default:
        if (action.path) {
          navigate(action.path);
        }
    }
  };

  // Handle stat card navigation
  const handleStatCardClick = (cardLabel: string) => {
    const routeMap: Record<string, string> = {
      "Total Customers": "/customers",
      "Active Projects": "/projects",
      "Total Tasks": "/tasks",
      "Total Employees": "/developers",
      "Total Invoices": "/invoice"
    };

    const route = routeMap[cardLabel];
    if (route) {
      navigate(route);
    }
  };

  // Get responsive column spans
  const getStatCardSpan = () => {
    if (isAdmin) {
      if (isMobile) return 24;
      if (isTablet) return 12;
      return 8;
    } else {
      if (isMobile) return 24;
      if (isTablet) return 12;
      return 12;
    }
  };

 

  const renderStatCards = () => {
    const cards = isAdmin ? getAdminStatCards() : getDevStatCards();
    const span = getStatCardSpan();

    return (
      <>
        <SectionLabel>My Overview</SectionLabel>
        <Row gutter={[isMobile ? 12 : 20, isMobile ? 12 : 20]} style={{ marginBottom: 28 }}>
          {cards.map((card) => (
            <Col xs={24} sm={12} lg={span} key={card.label}>
              <Card
                style={styles.card}
                bodyStyle={{ padding: isMobile ? "16px" : "20px" }}
                hoverable={Boolean(isAdmin)}
                onClick={() => isAdmin && handleStatCardClick(card.label)}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: isMobile ? 11 : 12, color: "#64748b", fontWeight: 600, marginBottom: 8 }}>{card.label}</div>
                    <div style={{ fontSize: isMobile ? 24 : 30, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>{card.value}</div>
                    {card.totalAmount && (
                      <div style={{ fontSize: isMobile ? 11 : 12, color: "#64748b", fontWeight: 500, marginTop: 4 }}>
                        Total: ₹{Number(card.totalAmount).toLocaleString('en-IN')}
                      </div>
                    )}
                  </div>
                  <div style={{
                    width: isMobile ? 40 : 48,
                    height: isMobile ? 40 : 48,
                    borderRadius: 14,
                    background: card.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: isMobile ? 18 : 20,
                    color: "#fff",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  }}>
                    {card.icon}
                  </div>
                </div>
                <div style={{ marginTop: isMobile ? 12 : 16, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                  {card.up
                    ? <ArrowUpOutlined style={{ color: "#10b981", fontSize: 12 }} />
                    : <ArrowDownOutlined style={{ color: "#ef4444", fontSize: 12 }} />}
                  <span style={{ color: card.up ? "#10b981" : "#ef4444", fontSize: isMobile ? 11 : 13, fontWeight: 500 }}>
                    {card.trend}
                  </span>
                  <span style={{ color: "#94a3b8", fontSize: isMobile ? 10 : 12 }}>vs last month</span>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </>
    );
  };

  const renderAdminContent = () => {
    if (!dashboardData) return null;

    const adminHrmsData = getAdminHrmsData();
    const adminTaskData = getAdminTaskData();
    const salesChartConfig = getSalesChartConfig();
    const expensesChartConfig = getExpensesChartConfig();
    const receiptsChartConfig = getReceiptsChartConfig();
    const leadsChartConfig = getLeadsChartConfig();
    const yearOptions = getSalesChartYearOptions();
    const monthOptions = getSalesChartMonthOptions();

    const getLayout = () => {
      if (isMediumLaptop) {
        return {
          graphCol: 24,
          quickActionsCol: 24,
          crmCol: 12,
          accountingCol: 12,
          activityCol: 8,
        };
      } else if (isLargeLaptop) {
        return {
          graphCol: 16,
          quickActionsCol: 8,
          crmCol: 8,
          accountingCol: 16,
          activityCol: 8,
        };
      } else {
        return {
          graphCol: 16,
          quickActionsCol: 8,
          crmCol: 8,
          accountingCol: 16,
          activityCol: 8,
        };
      }
    };

    const layout = getLayout();

    return (
      <>
        <Row gutter={[isMobile ? 12 : 20, isMobile ? 12 : 20]} style={{ marginBottom: 28 }}>
          <Col xs={24} lg={layout.graphCol}>
            <Card style={styles.card} bodyStyle={{ padding: isMobile ? "16px" : "20px" }}>
              <div style={{ ...styles.cardHeader, gap: 12, flexWrap: "wrap" }}>
                <div>
                  <Text style={styles.cardTitle}>Sales Chart</Text>
                </div>
                <Space wrap style={{ marginLeft: "auto" }}>
                  <Select
                    value={salesChartFilter}
                    onChange={(value) => {
                      const nextFilter = value as "yearly" | "monthly" | "quarterly";
                      setSalesChartFilter(nextFilter);
                    }}
                    style={{ minWidth: isMobile ? 140 : 170 }}
                    options={(dashboardData?.salesChart?.filters || []).map((item: { label: string; value: "yearly" | "monthly" | "quarterly" }) => ({
                      label: item.label,
                      value: item.value,
                    }))}
                  />
                  <Select
                    value={salesChartYear}
                    onChange={(value) => {
                      const nextYear = Number(value);
                      setSalesChartYear(nextYear);
                    }}
                    style={{ minWidth: isMobile ? 100 : 120 }}
                    options={yearOptions.map((year) => ({ label: String(year), value: year }))}
                  />
                  {salesChartFilter === "monthly" && (
                    <Select
                      value={salesChartMonth}
                      onChange={(value) => {
                        const nextMonth = String(value);
                        setSalesChartMonth(nextMonth);
                      }}
                      style={{ minWidth: isMobile ? 120 : 140 }}
                      options={monthOptions.map((month) => ({ label: month, value: month }))}
                    />
                  )}
                </Space>
              </div>

              <ReactECharts
                option={salesChartConfig.options}
                style={{ height: isMobile ? 260 : 320, width: "100%" }}
                notMerge
                lazyUpdate
              />
            </Card>
          </Col>
          <Col xs={24} lg={layout.quickActionsCol}>
            <Card style={styles.card} bodyStyle={{ padding: isMobile ? "16px" : "20px" }}>
              <div style={styles.cardHeader}>
                <Text style={styles.cardTitle}>Quick Actions</Text>
              </div>

              <Row gutter={[isMobile ? 8 : 12, isMobile ? 8 : 12]}>
                {adminQuickActions.map((action, i) => (
                  <Col span={12} key={i}>
                    <div
                      style={{
                        background: "#f8fafc",
                        borderRadius: 14,
                        padding: isMobile ? "12px 4px" : "16px 8px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: isMobile ? 4 : 8,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        border: "1px solid #f0f2f5",
                        transform: hoveredAction === i ? "translateY(-2px)" : "none",
                        boxShadow: hoveredAction === i ? "0 4px 12px rgba(0,0,0,0.05)" : "none",
                      }}
                      onMouseEnter={() => setHoveredAction(i)}
                      onMouseLeave={() => setHoveredAction(null)}
                      onClick={() => handleAdminQuickAction(action)}
                      // Restored accessibility handlers from your original code
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleAdminQuickAction(action);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={action.label}
                    >
                      <div
                        style={{
                          width: isMobile ? 32 : 40,
                          height: isMobile ? 32 : 40,
                          borderRadius: 10,
                          background: action.color, // Changed back to your original color setup
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: isMobile ? 16 : 18,
                        }}
                      >
                        {action.icon}
                      </div>
                      <span style={{ fontSize: isMobile ? 11 : 12, color: "#334155", textAlign: "center", fontWeight: 500 }}>
                        {action.label}
                      </span>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[isMobile ? 12 : 20, isMobile ? 12 : 20]} style={{ marginBottom: 28 }}>
          <Col xs={24}>
            <Card style={styles.card} bodyStyle={{ padding: isMobile ? "16px" : "20px" }}>
              <div style={{ ...styles.cardHeader, gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <Text style={styles.cardTitle}>Leads Chart (Total vs Converted to Sales)</Text>
                  <Space wrap>
                    <Select
                      value={leadsChartFilter}
                      onChange={(value) => {
                        const nextFilter = value as "yearly" | "monthly" | "quarterly";
                        setLeadsChartFilter(nextFilter);
                      }}
                      style={{ minWidth: isMobile ? 140 : 170 }}
                      options={(dashboardData?.leadsChart?.filters || [
                        { label: "Yearly", value: "yearly" },
                        { label: "Monthly", value: "monthly" },
                        { label: "Quarterly", value: "quarterly" }
                      ]).map((item: { label: string; value: "yearly" | "monthly" | "quarterly" }) => ({
                        label: item.label,
                        value: item.value,
                      }))}
                    />
                    <Select
                      value={leadsChartYear}
                      onChange={(value) => {
                        const nextYear = Number(value);
                        setLeadsChartYear(nextYear);
                      }}
                      style={{ minWidth: isMobile ? 100 : 120 }}
                      options={yearOptions.map((year) => ({ label: String(year), value: year }))}
                    />
                    {leadsChartFilter === "monthly" && (
                      <Select
                        value={leadsChartMonth}
                        onChange={(value) => {
                          const nextMonth = String(value);
                          setLeadsChartMonth(nextMonth);
                        }}
                        style={{ minWidth: isMobile ? 120 : 140 }}
                        options={monthOptions.map((month) => ({ label: month, value: month }))}
                      />
                    )}
                  </Space>
                </div>
                <Tag style={{ marginLeft: "auto", borderRadius: 999, padding: "4px 12px", background: "#eef2ff", border: "1px solid #c7d2fe", color: "#4338ca", fontWeight: 600 }}>
                  Total: Rs {formatAmount(leadsChartConfig.totalLeadsAmount)} | Converted: Rs {formatAmount(leadsChartConfig.totalConvertedAmount)}
                </Tag>
              </div>

              <ReactECharts
                key={`leads-${leadsChartFilter}-${leadsChartYear}-${leadsChartFilter === "monthly" ? leadsChartMonth : "all"}`}
                option={leadsChartConfig.options}
                style={{ height: isMobile ? 260 : 320, width: "100%" }}
                notMerge
                lazyUpdate
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[isMobile ? 12 : 20, isMobile ? 12 : 20]} style={{ marginBottom: 28 }}>
          <Col xs={24}>
            <Card style={styles.card} bodyStyle={{ padding: isMobile ? "16px" : "20px" }}>
              <div style={{ ...styles.cardHeader, gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <Text style={styles.cardTitle}>Expenses Chart</Text>
                  <Space wrap>
                    <Select
                      value={expensesChartFilter}
                      onChange={(value) => {
                        const nextFilter = value as "yearly" | "monthly" | "quarterly";
                        setExpensesChartFilter(nextFilter);
                      }}
                      style={{ minWidth: isMobile ? 140 : 170 }}
                      options={(dashboardData?.expensesChart?.filters || [
                        { label: "Yearly", value: "yearly" },
                        { label: "Monthly", value: "monthly" },
                        { label: "Quarterly", value: "quarterly" }
                      ]).map((item: { label: string; value: "yearly" | "monthly" | "quarterly" }) => ({
                        label: item.label,
                        value: item.value,
                      }))}
                    />
                    <Select
                      value={expensesChartYear}
                      onChange={(value) => {
                        const nextYear = Number(value);
                        setExpensesChartYear(nextYear);
                      }}
                      style={{ minWidth: isMobile ? 100 : 120 }}
                      options={yearOptions.map((year) => ({ label: String(year), value: year }))}
                    />
                    {expensesChartFilter === "monthly" && (
                      <Select
                        value={expensesChartMonth}
                        onChange={(value) => {
                          const nextMonth = String(value);
                          setExpensesChartMonth(nextMonth);
                        }}
                        style={{ minWidth: isMobile ? 120 : 140 }}
                        options={monthOptions.map((month) => ({ label: month, value: month }))}
                      />
                    )}
                  </Space>
                </div>
                <Tag style={{ marginLeft: "auto", borderRadius: 999, padding: "4px 12px", background: "#fff7ed", border: "1px solid #fdba74", color: "#c2410c", fontWeight: 600 }}>
                  Total: Rs {formatAmount(expensesChartConfig.totalExpensesAmount)}
                </Tag>
              </div>

              <ReactECharts
                option={expensesChartConfig.options}
                style={{ height: isMobile ? 260 : 320, width: "100%" }}
                notMerge
                lazyUpdate
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[isMobile ? 12 : 20, isMobile ? 12 : 20]} style={{ marginBottom: 28 }}>
          <Col xs={24}>
            <Card style={styles.card} bodyStyle={{ padding: isMobile ? "16px" : "20px" }}>
              <div style={{ ...styles.cardHeader, gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <Text style={styles.cardTitle}>Receipts Chart</Text>
                  <Space wrap>
                    <Select
                      value={receiptsChartFilter}
                      onChange={(value) => {
                        const nextFilter = value as "yearly" | "monthly" | "quarterly";
                        setReceiptsChartFilter(nextFilter);
                      }}
                      style={{ minWidth: isMobile ? 140 : 170 }}
                      options={(dashboardData?.receiptsChart?.filters || [
                        { label: "Yearly", value: "yearly" },
                        { label: "Monthly", value: "monthly" },
                        { label: "Quarterly", value: "quarterly" }
                      ]).map((item: { label: string; value: "yearly" | "monthly" | "quarterly" }) => ({
                        label: item.label,
                        value: item.value,
                      }))}
                    />
                    <Select
                      value={receiptsChartYear}
                      onChange={(value) => {
                        const nextYear = Number(value);
                        setReceiptsChartYear(nextYear);
                      }}
                      style={{ minWidth: isMobile ? 100 : 120 }}
                      options={yearOptions.map((year) => ({ label: String(year), value: year }))}
                    />
                    {receiptsChartFilter === "monthly" && (
                      <Select
                        value={receiptsChartMonth}
                        onChange={(value) => {
                          const nextMonth = String(value);
                          setReceiptsChartMonth(nextMonth);
                        }}
                        style={{ minWidth: isMobile ? 120 : 140 }}
                        options={monthOptions.map((month) => ({ label: month, value: month }))}
                      />
                    )}
                  </Space>
                </div>
                <Tag style={{ marginLeft: "auto", borderRadius: 999, padding: "4px 12px", background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#047857", fontWeight: 600 }}>
                  Total: Rs {formatAmount(receiptsChartConfig.totalReceiptsAmount)}
                </Tag>
              </div>

              <ReactECharts
                option={receiptsChartConfig.options}
                style={{ height: isMobile ? 260 : 320, width: "100%" }}
                notMerge
                lazyUpdate
              />
            </Card>
          </Col>
        </Row>

        <SectionLabel>Activity</SectionLabel>
        <Row gutter={[isMobile ? 12 : 20, isMobile ? 12 : 20]} style={{ marginBottom: 28 }}>
          <Col xs={24} lg={layout.activityCol}>
            <Card style={styles.card} bodyStyle={{ padding: isMobile ? "16px" : "20px" }}>
              <div style={styles.cardHeader}>
                <Text style={styles.cardTitle}>CRM / Leads</Text>
              </div>

              <Row gutter={[isMobile ? 12 : 16, isMobile ? 12 : 16]}>
                {crmData.map((item) => (
                  <Col span={12} key={item.label}>
                    <div style={{
                      background: "#f8fafc",
                      borderRadius: 14,
                      padding: isMobile ? "12px" : "16px",
                      height: "100%",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12 }}>
                        <div style={{
                          width: isMobile ? 32 : 40,
                          height: isMobile ? 32 : 40,
                          borderRadius: 10,
                          background: item.color + "15",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: item.color,
                          fontSize: isMobile ? 16 : 18,
                        }}>
                          {item.icon}
                        </div>
                        <div>
                          <div style={{ fontSize: isMobile ? 11 : 12, color: "#64748b", marginTop: 2 }}>{item.label}</div>
                          <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>{item.value}</div>
                          <div style={{ fontSize: isMobile ? 11 : 12, color: "#64748b", marginTop: 2 }}>{item.total}</div>
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={layout.activityCol}>
            <Card style={styles.card} bodyStyle={{ padding: isMobile ? "16px" : "20px" }}>
              <div style={styles.cardHeader}>
                <Text style={styles.cardTitle}>HRMS Overview</Text>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? "8px" : "10px" }}>
                {adminHrmsData.map((item, index) => (
                  <div key={index} style={{
                    background: "#f8fafc",
                    borderRadius: 14,
                    padding: isMobile ? "10px" : "12px",
                    minHeight: isMobile ? 72 : 82,
                    display: "flex",
                    alignItems: "center",
                    gap: isMobile ? 10 : 12,
                    border: "1px solid #eef2f6",
                    width: "100%"
                  }}>
                    <div style={{
                      width: isMobile ? 34 : 40,
                      height: isMobile ? 34 : 40,
                      borderRadius: 10,
                      background: item.bgColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: item.color,
                      fontSize: isMobile ? 16 : 18,
                      flexShrink: 0
                    }}>
                      {item.icon}
                    </div>
                    <div style={{ flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Text style={{ fontSize: isMobile ? 12 : 13, color: "#64748b", fontWeight: 500 }}>{item.label}</Text>
                      <Text style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color: "#1e293b" }}>{item.value}</Text>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={layout.activityCol}>
            <Card
              title={
                <div style={{ ...styles.cardHeader, gap: 8, flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Text style={styles.cardTitle}>Task Overview</Text>
                    {appliedFilterLabel && (
                      <Tag color="blue" style={{ fontSize: 11, fontWeight: 500 }}>
                        {appliedFilterLabel}
                      </Tag>
                    )}
                  </div>
                  {appliedFilterLabel ? (
                    <Button
                      type="text"
                      size="small"
                      onClick={() => {
                        setAppliedFilterLabel("");
                        setTaskFilterApplied(false);
                        fetchDashboardData(false);
                      }}
                      style={{
                        color: "#BCC6CC",
                        backgroundColor: "#00162B",
                        border: "1px solid #00162B",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 12px",
                        height: 32,
                        fontWeight: 500
                      }}
                    >
                      Clear
                    </Button>
                  ) : (
                    <Popover
                    open={filterPopoverVisible}
                    onOpenChange={setFilterPopoverVisible}
                    trigger="click"
                    placement="bottomRight"
                    content={
                      <div style={{ minWidth: 280, padding: "8px 0" }}>
                        <div style={{ marginBottom: 12, padding: "0 12px" }}>
                          <Text style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 6 }}>Filter Type</Text>
                          <Select
                            value={taskDateFilterType}
                            onChange={(value) => {
                              const nextFilter = value as "yearly" | "monthly" | "daily";
                              setTaskDateFilterType(nextFilter);
                            }}
                            style={{ width: "100%" }}
                            options={[
                              { label: "Year", value: "yearly" },
                              { label: "Month", value: "monthly" },
                              { label: "Day", value: "daily" }
                            ]}
                          />
                        </div>
                        <div style={{ marginBottom: 12, padding: "0 12px" }}>
                          <Text style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 6 }}>Year</Text>
                          <Select
                            value={taskDateYear}
                            onChange={(value) => {
                              const nextYear = Number(value);
                              setTaskDateYear(nextYear);
                            }}
                            style={{ width: "100%" }}
                            options={yearOptions.map((year) => ({ label: String(year), value: year }))}
                          />
                        </div>
                        {taskDateFilterType === "monthly" && (
                          <div style={{ marginBottom: 12, padding: "0 12px" }}>
                            <Text style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 6 }}>Month</Text>
                            <Select
                              value={taskDateMonth}
                              onChange={(value) => {
                                setTaskDateMonth(value as typeof monthNames[number]);
                              }}
                              style={{ width: "100%" }}
                              options={monthOptions.map((month) => ({ label: month, value: month }))}
                            />
                          </div>
                        )}
                        {taskDateFilterType === "daily" && (
                          <>
                            <div style={{ marginBottom: 12, padding: "0 12px" }}>
                              <Text style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 6 }}>Month</Text>
                              <Select
                                value={taskDateMonth}
                                onChange={(value) => {
                                  setTaskDateMonth(value as typeof monthNames[number]);
                                }}
                                style={{ width: "100%" }}
                                options={monthOptions.map((month) => ({ label: month, value: month }))}
                              />
                            </div>
                            <div style={{ marginBottom: 12, padding: "0 12px" }}>
                              <Text style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 6 }}>Day</Text>
                              <Select
                                value={taskDateDay}
                                onChange={(value) => {
                                  const nextDay = Number(value);
                                  setTaskDateDay(nextDay);
                                }}
                                style={{ width: "100%" }}
                                options={Array.from({ length: getDaysInMonth(taskDateYear, taskDateMonth) }, (_, i) => ({
                                  label: String(i + 1),
                                  value: i + 1
                                }))}
                              />
                            </div>
                          </>
                        )}
                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #f0f0f0", padding: "0 12px" }}>
                          <Button
                            type="primary"
                            size="middle"
                            block
                            onClick={() => {
                              setTaskFilterApplied(true);
                              setFilterPopoverVisible(false);

                              // Set the applied filter label based on the selected filter type
                              let filterLabel = "";
                              if (taskDateFilterType === "yearly") {
                                filterLabel = `Year: ${taskDateYear}`;
                              } else if (taskDateFilterType === "monthly") {
                                filterLabel = `${taskDateMonth} ${taskDateYear}`;
                              } else if (taskDateFilterType === "daily") {
                                filterLabel = `${taskDateDay} ${taskDateMonth} ${taskDateYear}`;
                              }
                              setAppliedFilterLabel(filterLabel);
                            }}
                            style={{
                              borderRadius: 8,
                              height: 36,
                              fontWeight: 500,
                              color: "#BCC6CC",
                              backgroundColor: "#00162B",
                              border: "none"
                            }}
                          >
                            Apply Filter
                          </Button>
                        </div>
                      </div>
                    }
                  >
                    <Button
                      type="text"
                      icon={<FilterOutlined />}
                      style={{
                        color: "#BCC6CC",
                        backgroundColor: "#00162B",
                        border: "1px solid #00162B",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 12px",
                        height: 32,
                        fontWeight: 500
                      }}
                    >
                      Filter
                    </Button>
                  </Popover>
                  )}
                </div>
              }
              style={{ ...styles.card, marginTop: 0, marginBottom: isMobile ? 12 : 20 }}
              headStyle={{ 
                borderBottom: "none",
                padding: "16px 20px 0 20px",  // Adjusted padding to match HRMS card
                minHeight: "auto"
              }}
              bodyStyle={{ padding: isMobile ? "16px" : "20px", paddingTop: isMobile ? "8px" : "12px" }}  // Reduced top padding
            >
              <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "16px" }}>
                {adminTaskData.map((item, index) => (
                  <div key={index}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isMobile ? 6 : 8 }}>
                      <Text style={{ fontSize: isMobile ? 13 : 14, fontWeight: 500, color: "#1e293b" }}>{item.label}</Text>
                      <Text style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#1e293b" }}>{item.value.toLocaleString()} ({item.percent}%)</Text>
                  </div>
                  <Progress
                    percent={item.percent}
                    strokeColor={item.color}
                    trailColor="#f1f5f9"
                    showInfo={false}
                    strokeLinecap="round"
                    size={["100%", 8]}
                      strokeWidth={8}
                  />
                </div>
              ))}
              </div>
            </Card>
          </Col>
        </Row>
      </>
    );
  };
  const renderDeveloperContent = () => {
    if (!dashboardData) return null;
    
    const devTaskStatsData = getDeveloperTaskStats();
    const developerTodayTasksData = getDeveloperTodayTasks();
    const developerAttendanceDataList = getDeveloperAttendanceData();
    const developerHrmsDataList = getDeveloperHrmsData();
    const developerAttendanceStatsObj = getDeveloperAttendanceStats();
    
    const totalTasksForDev = devTaskStatsData.reduce((sum, stat) => sum + stat.val, 0);
    const completedTasks = devTaskStatsData.find(t => t.label === "Completed")?.val || 0;
    const completionPercent = totalTasksForDev > 0 ? Math.round((completedTasks / totalTasksForDev) * 100) : 0;

    const getLayout = () => {
      if (isMediumLaptop) {
        return {
          taskOverviewCol: 24,
          attendanceCol: 24,
          hrmsCol: 24,
          quickActionsCol: 24,
        };
      } else if (isLargeLaptop) {
        return {
          taskOverviewCol: 16,
          attendanceCol: 8,
          hrmsCol: 12,
          quickActionsCol: 12,
        };
      } else {
        return {
          taskOverviewCol: 16,
          attendanceCol: 8,
          hrmsCol: 12,
          quickActionsCol: 12,
        };
      }
    };

    const layout = getLayout();

    return (
      <>
        <SectionLabel>Tasks & Attendance</SectionLabel>
        <Row gutter={[isMobile ? 12 : 20, isMobile ? 12 : 20]} style={{ marginBottom: 28 }}>
          <Col xs={24} lg={layout.taskOverviewCol}>
            <Card
              style={devCard}
              title={<CardTitle icon={<RocketOutlined />} color={P.indigo}>Task Overview</CardTitle>}
              bodyStyle={{ padding: isMobile ? "16px" : "20px" }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ flexShrink: 0 }}>
                  <Row gutter={[isMobile ? 12 : 16, isMobile ? 12 : 16]} style={{ marginBottom: 16 }}>
                    {devTaskStatsData.map(t => (
                      <Col span={isMobile ? 12 : (isMediumLaptop ? 12 : 6)} key={t.label}>
                        <div style={{ background: "#f8fafc", borderRadius: 14, padding: isMobile ? "12px" : "16px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                            <Text style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: "#334155" }}>{t.label}</Text>
                            <Text style={{ fontSize: isMobile ? 13 : 14, fontWeight: 800, color: t.color }}>{t.val}</Text>
                          </div>
                          <Progress
                            percent={totalTasksForDev > 0 ? Math.round((t.val / totalTasksForDev) * 100) : 0}
                            strokeColor={t.color}
                            trailColor="#e2e8f0"
                            showInfo={false}
                            strokeLinecap="round"
                            size={["100%", 8]}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                  <div style={{ background: "linear-gradient(90deg,#f0f9ff,#f0fdf4)", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 10 }}>
                    <FireOutlined style={{ color: P.amber, fontSize: 18 }} />
                    <Text style={{ fontSize: isMobile ? 12 : 13, color: "#334155" }}>
                      <strong>{completedTasks} / {totalTasksForDev}</strong> tasks done - <strong style={{ color: P.emerald }}>{completionPercent}%</strong> completion
                    </Text>
                  </div>
                </div>
                <Divider style={{ margin: 0 }} />
                <div style={{ maxHeight: isMobile ? "none" : 190, overflowY: isMobile ? "visible" : "auto", paddingRight: isMobile ? 0 : 6 }}>
                  <List
                    dataSource={developerTodayTasksData}
                    renderItem={(task: DevTaskType) => {
                      const sc = statusConfig[task.status];
                      return (
                        <List.Item style={{ padding: isMobile ? "10px 0" : "12px 4px", borderBottom: "1px solid #f1f5f9" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 14, width: "100%", flexWrap: isMobile ? "wrap" : "nowrap" }}>
                            <div
                              style={{
                                minWidth: isMobile ? 54 : 64,
                                height: isMobile ? 30 : 36,
                                padding: isMobile ? "0 10px" : "0 12px",
                                borderRadius: 10,
                                background: sc.bg,
                                color: "#1d4ed8",
                                border: `1.5px solid ${sc.border}`,
                                flexShrink: 0,
                                fontSize: isMobile ? 10 : 11,
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                lineHeight: 1,
                              }}
                            >
                              {getRawTaskId(task) || "-"}
                            </div>
                            <div style={{ flex: 1, minWidth: isMobile ? "100%" : 140 }}>
                              <Text style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: "#0f172a", display: "block" }}>{task.title}</Text>
                              <Text style={{ fontSize: isMobile ? 11 : 12, color: "#94a3b8" }}>{task.project}</Text>
                            </div>
                            <Space wrap style={{ marginTop: isMobile ? 8 : 0 }}>
                              <Tag style={{
                                borderRadius: 20, fontSize: isMobile ? 11 : 12, padding: "2px 12px",
                                background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, fontWeight: 600
                              }}>
                                {task.status}
                              </Tag>
                              <Tag style={{ borderRadius: 20, fontSize: isMobile ? 11 : 12, padding: "2px 12px", background: "#f1f5f9", color: "#64748b", border: "none" }}>
                                <ClockCircleOutlined style={{ marginRight: 4 }} />{task.deadline}
                              </Tag>
                            </Space>
                          </div>
                        </List.Item>
                      );
                    }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={layout.attendanceCol}>
            <Card
              style={{ ...devCard }}
              title={<CardTitle icon={<ClockCircleOutlined />} color={P.teal}>Today's Attendance</CardTitle>}
              extra={<Tag style={pill(developerAttendanceStatsObj.status === "Present" ? P.emerald : developerAttendanceStatsObj.status === "Absent" ? P.rose : P.sky, true)}>{developerAttendanceStatsObj.status}</Tag>}
              bodyStyle={{ padding: isMobile ? "16px" : "20px" }}
            >
              <Row gutter={[isMobile ? 12 : 16, isMobile ? 12 : 16]}>
                {developerAttendanceDataList.map(a => (
                  <Col span={12} key={a.label}>
                    <div style={{ background: "#f8fafc", borderRadius: 14, padding: isMobile ? "12px" : "16px" }}>
                      <div style={{ ...iconBox(a.color, isMobile ? 32 : 36), marginBottom: 10 }}>{a.icon}</div>
                      <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 800, color: "#0f172a" }}>{a.value}</div>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: "#94a3b8", marginTop: 2 }}>{a.label}</div>
                    </div>
                  </Col>
                ))}
              </Row>
              <Divider style={{ margin: "20px 0 16px" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                <Text style={{ fontSize: isMobile ? 12 : 13, color: "#64748b" }}>Expected: {developerAttendanceStatsObj.expectedHours}</Text>
                <Text style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: P.sky }}>Remaining: {developerAttendanceStatsObj.remainingHours}</Text>
              </div>
              <Progress
                percent={developerAttendanceStatsObj.progressPercent}
                strokeColor={{ from: "#0ea5e9", to: "#0d9488" }}
                trailColor="#e2e8f0"
                showInfo={false}
                strokeLinecap="round"
                size={["100%", 8]}
              />
            </Card>
          </Col>
        </Row>

        <SectionLabel>HRMS & Quick Actions</SectionLabel>
        <Row gutter={[20, 20]}>
          <Col xs={24} lg={layout.hrmsCol}>
            <Card
              style={devCard}
              title={<CardTitle icon={<TeamOutlined />} color={P.sky}>HRMS Overview</CardTitle>}
              bodyStyle={{ padding: isMobile ? "16px" : "20px" }}
            >
              <Row gutter={[isMobile ? 12 : 16, isMobile ? 12 : 16]}>
                {developerHrmsDataList.map(h => (
                  <Col span={12} key={h.label}>
                    <div style={{ 
                      background: "#f8fafc", 
                      borderRadius: 14, 
                      padding: isMobile ? "12px" : "16px", 
                      display: "flex", 
                      gap: isMobile ? 8 : 12, 
                      alignItems: "center",
                      border: "1px solid #eef2f6",
                      height: "100%",
                    }}>
                      <div style={{ 
                        ...iconBox(h.color, isMobile ? 36 : 40), 
                        borderRadius: 12, 
                        fontSize: isMobile ? 16 : 18,
                        flexShrink: 0,
                      }}>
                        {h.icon}
                      </div>
                      <div>
                        <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>{h.value}</div>
                        <div style={{ fontSize: isMobile ? 11 : 12, color: "#64748b", marginTop: 2 }}>{h.label}</div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={layout.quickActionsCol}>
            <Card
              style={{ ...devCard }}
              title={<CardTitle icon={<ThunderboltOutlined />} color={P.amber}>Quick Actions</CardTitle>}
              bodyStyle={{ padding: isMobile ? "16px" : "20px" }}
            >
              <Row gutter={[isMobile ? 12 : 16, isMobile ? 12 : 16]}>
                {devQuickActions.map((action, i) => (
                  <Col span={12} key={i}>
                    <div
                      style={{
                        background: action.grad,
                        borderRadius: 14,
                        padding: isMobile ? "12px 8px" : "16px 12px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: isMobile ? 4 : 8,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        transform: hoveredAction === i ? "translateY(-2px)" : "none",
                        boxShadow: hoveredAction === i ? "0 8px 20px rgba(0,0,0,0.15)" : "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                      onMouseEnter={() => setHoveredAction(i)}
                      onMouseLeave={() => setHoveredAction(null)}
                      onClick={() => {
                        if (action.path) {
                          handleDeveloperQuickAction(action);
                        }
                      }}
                      onKeyDown={(e) => {
                        if ((e.key === "Enter" || e.key === " ") && action.path) {
                          e.preventDefault();
                          handleDeveloperQuickAction(action);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={action.label}
                    >
                      <div style={{ 
                        width: isMobile ? 32 : 36, 
                        height: isMobile ? 32 : 36, 
                        borderRadius: 10, 
                        background: "rgba(255,255,255,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: isMobile ? 16 : 18,
                      }}>
                        {action.icon}
                      </div>
                      <span style={{ fontSize: isMobile ? 11 : 12, color: "#fff", textAlign: "center", fontWeight: 600 }}>{action.label}</span>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  if (!authData || loading) {
    return (
      <div style={styles.dashboardWrapper}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Text>Loading dashboard...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.dashboardWrapper}>
      
      {renderStatCards()}
      {isAdmin && renderAdminContent()}
      {isDeveloper && renderDeveloperContent()}

      {isDeveloper && (
        <>
          {isLeaveModalOpen && (
            <ApplyLeave
              visible={isLeaveModalOpen}
              onClose={() => setIsLeaveModalOpen(false)}
              onTaskCreated={() => {
                setIsLeaveModalOpen(false);
                fetchDashboardData(); // Refresh dashboard after action
              }}
              refreshLeaves={() => {
                setIsLeaveModalOpen(false);
                fetchDashboardData(); // Refresh dashboard after action
              }}
            />
          )}
          {isCampassOffModalOpen && (
            <AddCampassoffModal
              visible={isCampassOffModalOpen}
              onClose={() => setIsCampassOffModalOpen(false)}
              onSuccess={() => {
                setIsCampassOffModalOpen(false);
                fetchDashboardData(); // Refresh dashboard after action
              }}
            />
          )}
          {isOvertimeModalOpen && (
            <Addovertimemodal
              visible={isOvertimeModalOpen}
              onClose={() => setIsOvertimeModalOpen(false)}
              onTaskCreated={() => {
                setIsOvertimeModalOpen(false);
                fetchDashboardData(); // Refresh dashboard after action
              }}
            />
          )}
        </>
      )}

      {isAdmin && (
        <>
          {isAddCustomerModalOpen && (
            <AddCustomerModal
              visible={isAddCustomerModalOpen}
              onClose={() => setIsAddCustomerModalOpen(false)}
              onTaskCreated={() => {
                setIsAddCustomerModalOpen(false);
                fetchDashboardData(); // Refresh dashboard after action
              }}
            />
          )}
          {isAddProjectModalOpen && (
            <AddProjectModal
              visible={isAddProjectModalOpen}
              onClose={() => setIsAddProjectModalOpen(false)}
              onTaskCreated={() => {
                setIsAddProjectModalOpen(false);
                fetchDashboardData(); // Refresh dashboard after action
              }}
            />
          )}
          {isAddTaskModalOpen && (
            <TaskFormModal
              visible={isAddTaskModalOpen}
              onClose={() => setIsAddTaskModalOpen(false)}
              onTaskCreated={() => {
                setIsAddTaskModalOpen(false);
                fetchDashboardData(); // Refresh dashboard after action
              }}
            />
          )}
          {isAddExpenseModalOpen && (
            <AddExpenseModel
              visible={isAddExpenseModalOpen}
              onClose={() => setIsAddExpenseModalOpen(false)}
              onTaskCreated={() => {
                setIsAddExpenseModalOpen(false);
                fetchDashboardData(); // Refresh dashboard after action
              }}
            />
          )}
          {isAddEmployeeModalOpen && (
            <AddDeveloperModal
              visible={isAddEmployeeModalOpen}
              onClose={() => setIsAddEmployeeModalOpen(false)}
              onTaskCreated={() => {
                setIsAddEmployeeModalOpen(false);
                fetchDashboardData(); // Refresh dashboard after action
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DashboardContent;

















